import { makeStyles } from "@material-ui/core/styles";
import { mediaQueries } from "shared/config/breakpoints";
import styled from "styled-components";

// Trigger button that opens a nav group's dropdown. Styled to sit inline with
// the plain NavLinks and match their spacing / typography.
//
// Triggers only render in the collapsed layout (<= NavCollapse). There are only
// a few short triggers, so unlike the flat links they don't need their font
// shrunk on smaller screens — spacing tightens but the label stays full size.
// The small right padding keeps the caret clear of NavLinksGroup's clipping edge.
//
// Safety net for very narrow screens (< ~345px): only the first trigger (the one
// next to the logo) may shrink, ellipsizing its label ("Res…") instead of being
// clipped mid-word against the logo. The other triggers always stay complete.
export const NavGroupTrigger = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    background: none;
    border: none;
    padding: 0 0.25rem 0 0;
    margin-left: 0.75em;
    height: 1.25rem;
    cursor: pointer;
    color: ${globalThis.config.colors.black};
    white-space: nowrap;
    flex: 0 0 auto;

    &:first-of-type {
        flex: 0 1 auto;
        min-width: 0;
    }

    & .MuiTypography-root {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    ${mediaQueries.small} {
        margin-left: 0.5em;
    }

    /* On phones, drop the right padding and trim the caret's built-in
       whitespace (its 16px box only draws an 8px chevron) to win back width. */
    ${mediaQueries.superSmall} {
        margin-left: 0.4em;
        padding-right: 0;

        & svg {
            margin: 0 -0.15rem;
        }
    }

    &:focus-visible {
        outline: 1px solid ${globalThis.config.colors.black};
        outline-offset: 2px;
    }

    & svg {
        flex-shrink: 0;
        transition: transform 0.15s ease;
    }

    &[data-open="true"] svg {
        transform: rotate(180deg);
    }
`;

// rc-menu (@szhsin/react-menu) theming for the dropdown list. Mirrors the look
// of shared/components/inputs/menu/MoreMenu.jsx so nav dropdowns feel native.
export const useNavGroupMenuStyles = makeStyles(() => ({
    menu: {
        "&.rc-menu": {
            border: globalThis.config.border.medium,
            minWidth: "9rem",
            padding: 0,
            paddingTop: "0.5rem",
            paddingBottom: "0.5rem",
            zIndex: 1070,
            backgroundColor: globalThis.config.colors.white,
        },
        "& .rc-menu__item": {
            padding: "0.375rem 1rem",
            fontSize: globalThis.config.fonts.sizes.smedium,
            fontFamily: globalThis.config.fonts.families.regular,
            color: globalThis.config.colors.black,
            "& > a": {
                color: globalThis.config.colors.black,
                textDecoration: "none",
            },
        },
        "& .rc-menu__item--hover": {
            backgroundColor: globalThis.config.colors.gray8,
        },
        // Current section: override szhsin's default blue (#007bff / white text)
        // with a subtle gray fill and black text.
        "& .rc-menu__item--active": {
            color: globalThis.config.colors.black,
            backgroundColor: globalThis.config.colors.gray6,
            fontFamily: globalThis.config.fonts.families.medium,
        },
        "& .rc-menu__divider": {
            margin: "0.375rem 0",
        },
    },
}));
